// Helpers for date-series trend charts that need a TRUE time-scale x-axis.
//
// Why this exists: a categorical x-axis (dataKey = a date *string*) gives every
// row an equal-width slot regardless of its real date, so any missing days get
// silently compressed — plotted points drift away from their true calendar
// position and stop lining up with the axis labels. Plotting against an epoch-ms
// x-value (type="number" scale="time") fixes that: each day sits at its real
// calendar distance, and missing days render as an actual gap.
//
// First proven inline in google-analytics/SessionsTrendChart.js; extracted here so
// the Ads SpendTrend + GA4 channel drill-down charts share one implementation
// instead of re-copying the gap-detection loop.

const DAY_MS = 86400000;

// "YYYY-MM-DD" → "Mon D" for axis ticks and tooltips. Parses at LOCAL midnight
// (the explicit T00:00:00) so the label matches the calendar day the user means.
export const fmtTrendDate = (date) =>
  new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

// "YYYY-MM-DD" → local-midnight epoch (ms). This is the numeric x-value.
export const dateToTs = (date) => new Date(date + 'T00:00:00').getTime();

// epoch (ms) → "YYYY-MM-DD" using LOCAL components, NOT toISOString() (which
// shifts to UTC and can land on the wrong calendar day — the toLocalDateStr
// pitfall called out in CLAUDE.md).
export const tsToDateStr = (ts) => {
  const date = new Date(ts);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

// Given chart rows already carrying a numeric `ts` (epoch ms) sorted ascending,
// return the multi-day gaps as [{ x1, x2, label }] ready to hand to <ReferenceArea>.
// Bounds are nudged half a day inward so the band sits strictly between the
// last-known and next-known points. `label` is a human "No data: …" string for the
// SVG <title> hover.
export const findTrendGaps = (rows) => {
  const gaps = [];
  for (let index = 0; index < rows.length - 1; index++) {
    const gapMs = rows[index + 1].ts - rows[index].ts;
    if (gapMs > DAY_MS) {
      const firstMissing = fmtTrendDate(tsToDateStr(rows[index].ts + DAY_MS));
      const lastMissing = fmtTrendDate(tsToDateStr(rows[index + 1].ts - DAY_MS));
      gaps.push({
        x1: rows[index].ts + DAY_MS / 2,
        x2: rows[index + 1].ts - DAY_MS / 2,
        label: firstMissing === lastMissing ? `No data: ${firstMissing}` : `No data: ${firstMissing} – ${lastMissing}`,
      });
    }
  }
  return gaps;
};

// Shared <XAxis> props for a time-scale date axis. Spread onto the element:
//   <XAxis {...timeAxisProps()} />
// Pass overrides (e.g. a tighter minTickGap for a short chart) as the argument.
export const timeAxisProps = (overrides = {}) => ({
  dataKey: 'ts',
  type: 'number',
  scale: 'time',
  domain: ['dataMin', 'dataMax'],
  tickFormatter: (ts) => fmtTrendDate(tsToDateStr(ts)),
  tick: { fontSize: 11, fill: '#9ca3af' },
  minTickGap: 24,
  axisLine: { stroke: '#374151' },
  tickLine: false,
  ...overrides,
});
